<script setup lang="ts">
import type { NuxtLinkProps } from '#app';

const props = defineProps<{
  theme?: 'primary' | 'secondary' | 'white' | 'outline' | 'red';
  size?: 'small' | 'standard' | 'large' | 'xl';
  disabled?: boolean;
  download?: string;
  isLoading?: boolean;
  to?: NuxtLinkProps['to'];
  target?: NuxtLinkProps['target'];
}>();

const emit = defineEmits(['click']);

const handleClick = (e: Event) => {
  if (!props.disabled && !props.isLoading) {
    emit('click', e);
    if (props.download && props.to && typeof props.to === 'string') {
      const a = document.createElement('a');
      a.href = props.to;
      a.download = props.download;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      e.preventDefault();
      e.stopPropagation();
    }
  } else {
    e.preventDefault();
    e.stopPropagation();
  }
};
</script>

<template>
  <NuxtLink
    :class="[
      $style.button,
      {
        [$style[`button_size_${props.size}`]!]: props.size,
        [$style[`button_theme_${props.theme}`]!]: props.theme,
        [$style.button_disabled!]: props.disabled || props.isLoading,
        [$style.button_loading!]: props.isLoading,
      },
    ]"
    :to="props.to"
    :target="props.target"
    :aria-disabled="props.disabled"
    @click="handleClick"
  >
    <span :class="{ [$style.button_hidden!]: props.isLoading }">
      <slot />
    </span>
    <LoaderSimple v-if="props.isLoading" :class="$style.loader" />
  </NuxtLink>
</template>

<style lang="scss" module>
.button {
  display: flex;
  padding-left: var(--space-2-5x);
  padding-right: var(--space-2-5x);
  justify-content: center;
  align-items: center;
  text-decoration: none;
  transition: all 0.2s;

  &_hidden {
    visibility: hidden;
  }

  &_size {
    &_small {
      height: 32px;
      min-height: 32px;
      padding-left: var(--space-2-5x);
      padding-right: var(--space-2-5x);
      @include text_button3;
    }
    &_standard {
      height: 40px;
      min-height: 40px;
      padding-left: var(--space-3x);
      padding-right: var(--space-3x);
      @include text_button2;
    }
    &_large {
      height: 48px;
      min-height: 48px;
      padding-left: var(--space-4x);
      padding-right: var(--space-4x);
      @include text_button1;
    }
    &_xl {
      height: 60px;
      min-height: 60px;
      padding-left: var(--space-4x);
      padding-right: var(--space-4x);
      @include text_button1;
    }
  }

  &_theme {
    &_primary {
      background-color: var(--color-black-1);
      color: var(--color-white);

      &.button_disabled {
        cursor: default;
        background-color: var(--color-gray-2);
      }
    }
    &_secondary {
      background: var(--color-gray-line);
      color: var(--color-black-1);

      &.button_disabled {
        cursor: default;
        background-color: var(--color-gray-bg);
        color: var(--color-gray-2);
      }
    }
    &_white {
      background: var(--color-white);
      color: var(--color-black-1);

      &.button_disabled {
        cursor: default;
        background-color: var(--color-white);
        color: var(--color-gray-2);
      }
    }
    &_red {
      background: var(--color-red-1);
      color: var(--color-white);

      &.button_disabled {
        cursor: default;
        background-color: var(--color-red-1);
        color: var(--color-gray-2);
      }
    }
    &_outline {
      background: var(--color-white);
      color: var(--color-black-1);
      border: 1px solid var(--color-gray-line);

      &.button_disabled {
        cursor: default;
        background-color: var(--color-white);
        color: var(--color-gray-2);
        border: 1px solid var(--color-gray-line);
      }
    }
  }

  &__heading {
    user-select: none;
    &_bold {
      font-weight: 500;
      letter-spacing: -0.48px;
    }
  }
}

@media screen and (min-width: 1025px) {
  .button {
    &:not(&_disabled),
    &:not(&_loading) {
      cursor: pointer;
    }

    &_theme {
      &_primary {
        &:hover,
        &:active {
          background-color: var(--color-black-2);
          color: var(--color-white);
        }

        &.button_disabled:hover,
        &.button_disabled:active {
          cursor: default;
          background-color: var(--color-gray-2);
        }
      }
      &_secondary {
        &:hover {
          background: var(--color-gray-2);
        }

        &.button_disabled:hover,
        &.button_disabled:active {
          cursor: default;
          background-color: var(--color-gray-bg);
          color: var(--color-gray-2);
        }
      }
      &_white {
        &:hover {
          background-color: var(--color-gray-bg);
          color: var(--color-black-1);
        }

        &.button_disabled:hover,
        &.button_disabled:active {
          cursor: default;
          background-color: var(--color-white);
          color: var(--color-gray-2);
        }
      }
      &_outline {
        &:hover {
          background-color: var(--color-gray-bg);
          color: var(--color-black-1);
          border-color: transparent;
        }

        &.button_disabled:hover,
        &.button_disabled:active {
          cursor: default;
          background-color: var(--color-white);
          color: var(--color-gray-2);
          border: 1px solid var(--color-gray-line);
        }
      }
    }
  }
}
@media (max-width: 479px) {
  .button {
    width: 100%;
  }
}
</style>
